// ============================================================
// AddDomainDialog.js
// Simple modal for adding one federated domain to an active
// TenantFederationConfig (W3.5 commit 3/3). Powers FederationTab's
// "Add domain" button in the active-state domain list.
//
// Validation matches the server's DOMAIN_REGEX. The server is the
// ultimate authority — `federated_domain_already_configured` (409)
// and `invalid_domain_format` (400) responses are surfaced inline
// in this dialog rather than via a toast so the operator can fix
// and re-submit without losing context.
// ============================================================

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import Field from './Field';

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

export default function AddDomainDialog({ isOpen, onClose, onSubmit }) {
  const [domain, setDomain] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the dialog re-opens so a previous error
  // doesn't bleed into the next attempt.
  useEffect(() => {
    if (isOpen) {
      setDomain('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  function validate(value) {
    if (!value) return 'Required';
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return 'Domain only — no protocol prefix';
    }
    if (!DOMAIN_REGEX.test(value)) return 'Not a valid email domain';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = domain.trim();
    const validationError = validate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed.toLowerCase());
      // onSubmit's success path is responsible for calling onClose.
    } catch (err) {
      setSubmitting(false);
      // Surface server-side conflict / format errors inline.
      const status = err && err.status;
      const code   = err && err.error;
      if (status === 409 && code === 'federated_domain_already_configured') {
        const conflictTenantId = err.existingTenantId
          || (err.body && err.body.existingTenantId);
        setError(conflictTenantId
          ? `This domain is already configured on Tenant #${conflictTenantId}.`
          : 'This domain is already configured on another Tenant.');
        return;
      }
      if (status === 400 && code === 'invalid_domain_format') {
        setError('Not a valid email domain.');
        return;
      }
      // Generic fallback — surface the server message if any.
      setError((err && err.detail) || (err && err.message) || 'Something went wrong. Please try again.');
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add federated domain"
      closeDisabled={submitting}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Field
          label="Email domain"
          help="Email domain of users who will sign in via federation. Must be globally unique across all tenants."
          error={error}
          required
        >
          <input
            type="text"
            value={domain}
            onChange={(e) => { setDomain(e.target.value); if (error) setError(null); }}
            placeholder="example.co.uk"
            autoComplete="off"
            autoFocus
            data-testid="add-domain-dialog-input"
          />
        </Field>

        <div className="confirm-dialog__actions">
          <button
            type="button"
            className="confirm-dialog__cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="confirm-dialog__confirm confirm-dialog__confirm--primary"
            disabled={submitting}
          >
            {submitting ? 'Adding…' : 'Add domain'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
